import { useEffect } from 'react';

/**
 * Run an effect EXACTLY once when the component mounts. The optional
 * cleanup runs on unmount.
 *
 * This is the only sanctioned escape hatch from the project's
 * "no direct `useEffect`" rule (see `.claude/skills/no-use-effect/SKILL.md`).
 * Use it only for true mount-time external system setup: DOM integration,
 * third-party widget initialization, browser API subscriptions, AI SDK
 * stream kickoff, etc.
 *
 * The intentionally-empty dependency array means the effect WILL NOT
 * re-run when callbacks/values change. Capture latest values via refs or
 * store hooks if you need them inside the effect body.
 */
/**
 * Cleanup callback type. Returning nothing is allowed too; use `undefined`
 * (rather than `void`) in the union so the rule against `void` in unions is
 * satisfied.
 */
type MountEffectCallback = () => undefined | (() => void);

// eslint-disable-next-line react-hooks/exhaustive-deps
export const useMountEffect = (effect: MountEffectCallback): void => useEffect(effect, []);
